import { ApplicationState } from "../../../store";
import { ToxnotComponentProperties } from "../BaseApplicationComponents/ToxnotComponent";
import * as SurveyResponseController from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseController";
import { InventoryQuestionnaireType, SurveyResponseState } from '../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseStates';
import { AISurveyAgentState } from '../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController';
import { DataCollectionAiAgentOperationType, QuestionnairePageType, QuestionnaireQuestion, QuestionRegulatoryData, SurveyPageType, SurveyType, QuestionRevision, QuestionType, QuestionnaireProcessingType } from '../../../store/Models/Surveys/SurveyDataModel';
import Question from '../Question';
import { FileTableEntry } from '../../../store/Models/Documents/DocumentModels';
import { ToxnotSignInRequiredComponent } from "../BaseApplicationComponents/ToxnotSignInRequiredComponent";
import { TeamRole } from '../../../store/Models/UserContext';
import RevisionRequest from '../RevisionRequest/RevisionRequest';
import {SelectedInventoriesManager} from '../../../store/Managers/SelectedInventoriesManager';
import MaterialSummary from '../MaterialSummary/MaterialSummary';
import {ToxnotServiceClient} from "../../../store/ToxnotServiceClient";
import { getActiveAIOperationFromState } from "../../../store/StateControllers/Survey/SurveyResponse/AISurveyAgentController";
import {ComplianceMaterialSummaryNode, RegulationScoresTable, isSurveyReadonly} from "../../../store/StateControllers/Survey/SurveyResponse/SurveyResponseModels";
import {LoadingSpinner, LoadingSpinnerSize} from "../LoadingSpinner";
import {connect} from "react-redux";
import ExtensionRequest from "../ExtensionRequest/extensionRequest";
import ScipSurveyPage from "../ScipSurveyPage/ScipSurveyPage";
import CommonModalEditor from '../Common/CommonModalEditor';
import AIButton from '../../common/AIButton';
import { RegulationManager } from '../../../store/Managers/RegulationManager';

import "./styles.css";

interface LocalProps {
    questionsList: QuestionnaireQuestion[];
    questionRegulatoryData: QuestionRegulatoryData[];
    onResponseChange?: (
        value: string,
        valueComment: string,
        questionIndex: number,
        files?: FileTableEntry[],
        attchedFileIds?: string[]
    ) => void;
    pageTitle: string;
    regulationId: string;
    fileList: FileTableEntry[];
    inventoryId?: string;
    pageRevisionInstructions?: string;
    questionsRevisionInstructions?: QuestionRevision[] | null;
    questionnairePageType?: QuestionnairePageType;
    onComponentDidMount: () => Promise<void>;
}

type QuestionnairePageProps =
    ToxnotComponentProperties &
    typeof SurveyResponseController.actionCreators &
    SurveyResponseState &
    { aiSurveyAgentState?: AISurveyAgentState } &
    LocalProps;

interface LocalState {
    regulationScoresTable: RegulationScoresTable | null;
    selectedMaterialComplianceNode: ComplianceMaterialSummaryNode | null;
    isDataLoaded: boolean;
    plausibilityData: any;
}

class QuestionnairePage extends ToxnotSignInRequiredComponent<QuestionnairePageProps, LocalState> {
    private _serviceClient: ToxnotServiceClient;

    public constructor(props: QuestionnairePageProps) {
        super(props);

        this.state = {
            regulationScoresTable: null,
            selectedMaterialComplianceNode: null,
            isDataLoaded: false,
            plausibilityData: null
        };

        this._serviceClient = new ToxnotServiceClient();
    }

    public async componentDidMount() {
        const promises: Promise<void>[] = []

        if (this.props.onComponentDidMount) {
            promises.push(this.props.onComponentDidMount());
        }

        if (this.props.selectedInventory) {
            promises.push(this.fetchAllComplianceScores());
            promises.push(this.fetchPlausibilityData());
        }

        if (this.isScipPage()) {
            promises.push(this.handleScipQuestionniarePage());
        }

        await Promise.all(promises);

        // initializes a regulation manager based on the questionniare type
        this.props.initializeRegulationManager(
            this.props.regulationId,
            this.props.questionsList,
            this.props.questionRegulatoryData,
            this.state.regulationScoresTable
        );


        this.setState({
            isDataLoaded: true
        });
    }

    private isScipPage = () => (this.props.selectedInventory && (this.getActiveInventoryQuestionnairePage().type === QuestionnairePageType.ScipQuestionnaire));

    private getActiveInventoryQuestionnairePage = () => this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex].pages[this.props.navContext.surveyQuestionnairePageIndex];

    private async handleScipQuestionniarePage() {
        await this.props.fetchScipSubmissionForSelectedInventory();
    }

    private async fetchAllComplianceScores() {
        if (!this.props.selectedInventory) {
            throw new Error("Inventory not set");
        }

        const apiResult = await this._serviceClient.inventoryComplianceScores({
            inventoryId: this.props.selectedInventory.inventoryId,
            flagZerosAndMissingPercentagesOnSubstances: true
        });

        if (!apiResult.success || !apiResult.data) {
            return;
        }

        this.setState({
            regulationScoresTable: apiResult.data.complianceScoreResults,
            selectedMaterialComplianceNode: apiResult.data.complianceScoreResults[this.props.regulationId]?.materialsRootNode ?? null
        });
    }

    private async fetchPlausibilityData() {
        if (!this.props.selectedInventory) {
            throw new Error("Inventory not set");
        }

        const serviceClient = new ToxnotServiceClient();

        var plausibilityResponse = await serviceClient.getPlausibilityData(this.props.selectedInventory?.requestorOwnedInventoryId || "", this.props.coreData?.request?.ownerId || "");
        if (plausibilityResponse.success) {
            if (plausibilityResponse.data !== null) {
                this.setState({
                    plausibilityData: plausibilityResponse.data
                })
            }
        }
    }

    protected getDisallowedRoles(): TeamRole[] {
        return [TeamRole.Marketing, TeamRole.Viewer];
    }

    private getDisplayIndex(questionIndex: number, question: QuestionnaireQuestion): number {
        const { questionsList } = this.props;

        // Count hidden questions, subsections, and sections before the current index.
        const hiddenCount = questionsList
            .slice(0, questionIndex)
            .filter(q =>
                q.hidden ||
                q.type === QuestionType.Subsection ||
                q.type === QuestionType.Section
            ).length;

        // If no sections exist, subtract the count of hidden questions.
        if (!questionsList.some(q => q.type === QuestionType.Section)) {
            return questionIndex - hiddenCount;
        }

        // Find the index of the last Section before the current question.
        const lastSectionIndex = questionsList
            .slice(0, questionIndex)
            .reduce((lastIndex, currentQuestion, idx) =>
                currentQuestion.type === QuestionType.Section ? idx : lastIndex,
                -1
            );

        // If a section exists, count hidden items between last section and current question
        if (lastSectionIndex !== -1) {
            const hiddenCountInSection = questionsList
                .slice(lastSectionIndex + 1, questionIndex)
                .filter(q =>
                    q.hidden ||
                    q.type === QuestionType.Subsection ||
                    q.type === QuestionType.Section
                ).length;

            return questionIndex - lastSectionIndex - 1 - hiddenCountInSection;
        }

        return questionIndex - hiddenCount;
    }

    private onAiHelpfulStateChange = async (questionIndex: number, isHelpful: -1 | 0 | 1): Promise<void> => {
        this.props.updateQuestionAiHelpfulState(isHelpful, questionIndex);
    }

    private getConditionalOnQuestionIndex(question: QuestionnaireQuestion): number {
        let parentQuestionIndex = question?.conditionalOnQuestion?.questionIndex;

        if (!parentQuestionIndex) {
            return 0;
        }

        const { questionsList } = this.props;

        const hiddenOrSubsectionCount = questionsList
            .slice(0, parentQuestionIndex)
            .filter(q => {
              if (q.conditionalOnQuestion) {
                const grandParentIndex = this.getConditionalOnQuestionIndex(q);
                const grandParentValue = questionsList[grandParentIndex]?.value || '';
                if (!grandParentValue) return true; // Empty grandparent: treat as hidden
              }
              return q.hidden || q.type === QuestionType.Subsection;
            }).length;

        return parentQuestionIndex - hiddenOrSubsectionCount;
    }

    private setLocalStorageItems() {
        localStorage.setItem("select-inventories-action", "surveyResponseImport");
        localStorage.setItem("import-survey-response-id", this.props.responseId!);
        localStorage.setItem("import-survey-response-inventory-id", this.props.selectedInventory?.inventoryId!);
        localStorage.setItem("import-survey-response-return-url", window.location.href);
    }
    
    private handleClickImportMaterial = () => {
        //localStorage.setItem("customer-last-active-tab", "current-tab"); //todo : find the current tab
        this.setLocalStorageItems();

        SelectedInventoriesManager.navigateToSelectInventoriesPage(this.getUserContextForSignedInUser().userId, this.localizeString("Select Products and Materials"));
    }
    
    private renderQuestions = () => {
        const questionsList = this.props.questionsList || [];
        const visibleQuestions = questionsList.filter(q => !q.hidden);

        // Edge case: No visible questions
        if (visibleQuestions.length === 0) {
            return null;
        }

        return visibleQuestions.map((q, visibleIndex) => {
            // Find original index by matching the question object reference
            const originalIndex = questionsList.findIndex(originalQ => originalQ === q);
            if (originalIndex === -1) {
                return null;
            }

            const questionProps = this.getQuestionProps(originalIndex, q);

            return (
                <div key={`${this.props.pageTitle}-${originalIndex}`} className="survey-question m-b-20">
                    <Question {...questionProps} />
                </div>
            );
        });
    };

    private getQuestionnaireProcessingType = () => {
            const legacyQuestionnaireTypes = [
                QuestionnairePageType.ConflictMinerals,
                QuestionnairePageType.ConflictMineralsV2,
                QuestionnairePageType.ReachAuthorisationCompliance,
                QuestionnairePageType.ReachAuthorisationV2Compliance,
                QuestionnairePageType.RoHSCompliance,
                QuestionnairePageType.ReachRohsStatement,
                QuestionnairePageType.REACHCompliance,
                QuestionnairePageType.CaProp65
            ];

            if (this.props.navContext.mainNavIndex === SurveyPageType.SupplierQuestionnaire) {
                return QuestionnaireProcessingType.Supplier;
            }
    
            if (this.getActiveInventoryQuestionnairePage().type  === QuestionnairePageType.Custom) {
                return QuestionnaireProcessingType.Custom_BOM;
            }
    
            if (this.getActiveInventoryQuestionnairePage().type === QuestionnairePageType.Attestation) {
                return QuestionnaireProcessingType.Custom_Attestation;
            }

            if (this.isScipPage()) {
                return QuestionnaireProcessingType.Scip;
            }

            if (this.getActiveInventoryQuestionnairePage().type == QuestionnairePageType.DynamicRegulation) {
                return QuestionnaireProcessingType.Dynamic;
            }

            if(legacyQuestionnaireTypes.containsAnyOf([this.getActiveInventoryQuestionnairePage().type])) {
                return QuestionnaireProcessingType.Legacy;  
            }

            return null;
        };

    private getQuestionProps = (originalIndex: number, q: QuestionnaireQuestion) => {
        const baseProps = {
            appServices: this.props.appServices,
            regulationLogicManager: this.props.regulationManager!,
            pageTitle: this.localizeString(this.props.pageTitle),
            onResponseChange: this.props.onResponseChange,
            onAiHelpfulStateChange: this.onAiHelpfulStateChange,
            localizer: this.props.appServices,
            plausibilityData: this.state.plausibilityData,
            surveyState: this.props.coreData?.response.state,
            responseId: this.props.responseId,
            regulationId: this.props.regulationId,
            questionnaireProcessingType: this.getQuestionnaireProcessingType(),
        };

        const questionSpecificProps = {
            questionIndex: originalIndex,
            question: q,
            displayIndex: this.getDisplayIndex(originalIndex, q),
            parentDisplayIndex: this.getConditionalOnQuestionIndex(q),
        };

        const conditionalProps = {
            hasIngredientDisclosure: this.props.selectedInventory?.hasBomPage,
            bomHasAtLeastOneIngredient: this.props.selectedInventory
                ?.inventoryRows
                .some(r => (r.assignedSubstanceId !== null) || (r.assignedPrivateSubstanceId !== null) || (r.overrideCasNumber !== null))
                ?? false,
            fileList: this.props.fileList,
            inventoryId: this.props.navContext.mainNavIndex === SurveyPageType.SupplierQuestionnaire
                ? undefined
                : this.props.inventoryId,
            revisionInstructions: this.props.questionsRevisionInstructions?.firstOrDefault(
                r => r.pageIndex === this.props.navContext.surveyQuestionnairePageIndex &&
                    r.questionIndex === originalIndex
            )?.revisionInstructions,
            language: this.props.insActiveLanguage,
            isDisabled: isSurveyReadonly(this.props.coreData, getActiveAIOperationFromState(this.props.aiSurveyAgentState, DataCollectionAiAgentOperationType.Survey)), // submitted
            supplierRequiredToDiscloseRegulatoryIngredients: this.props.coreData?.request?.metadata
                ?.requireSuppliersToDiscloseRegulatoryIngredients ?? false,
            navigateToBomPage: () => this.props.setSurveyBomPage(this.props.navContext.inventoryNavIndex),
            extensionRequested: this.props.coreData?.extensionRequest?.inventoryIds?.some(
                i => i === this.props.selectedInventory?.inventoryId
            ),
            extensibleColumnNames: this.props.selectedInventory?.extensibleColumnNames ??
                                this.props.coreData?.request?.bomTemplateConfiguration?.extensibleColumns ??
                                [],
        };

        return { ...baseProps, ...questionSpecificProps, ...conditionalProps };
    };

    protected renderConcreteComponent(): JSX.Element | null {
        if ((this.props.selectedInventory && (this.state.regulationScoresTable === null)) || (this.props.regulationManager == null) || !this.state.isDataLoaded) {
            return <LoadingSpinner size={LoadingSpinnerSize.Medium} />
        }
        
        const info = this.props.regulationManager.getPageRequiredInfoLocalized();

        const shouldMaterialSummaryAppear = 
            this.props.navContext.mainNavIndex === SurveyPageType.Inventory &&
            this.props.selectedInventory?.hasBomPage && 
            this.props.surveyQuestionnairesList[this.props.navContext.surveyQuestionnaireIndex].pages[this.props.navContext.surveyQuestionnairePageIndex].regulationId &&
            !this.isScipPage();

        return (
            <>
                <div className="m-b-10 page-title-border flex-space-between">
                    <h4 className="m-t-10 m-b-10">{this.localizeString(this.props.pageTitle)}</h4>
                    <div className="import-container">
                        <div className="m-b-10">
                            <AIButton
                                className="m-r-5"
                                pageContext={this.props.navContext.mainNavIndex === SurveyPageType.SupplierQuestionnaire ? "supplier_questionnaire" : "questionnaire"}
                            />
                            {this.props.selectedInventory?.hasBomPage &&
                                <>
                                    <button className="btn btn-default m-r-5 import-button" onClick={() => { $("#import-btns-modal-qPage").modal("show"); }}>{this.localizeString("Import")}
                                        <span className="import-button-icon glyphicon glyphicon-download-alt">
                                        </span>
                                    </button >
                                    <CommonModalEditor modalId={"import-btns-modal-qPage"} localizer={this.props.appServices} navContext={this.props.navContext} responseId={this.props.responseId} selectedInventory={this.props.selectedInventory} userId={this.getUserContextForSignedInUser().userId} handleClickImportMaterial={this.handleClickImportMaterial}></CommonModalEditor>
                                </>
                            }
                            {this.props.questionnairesTypes[this.props.navContext.surveyQuestionnaireIndex] !== InventoryQuestionnaireType.AttestationQuestionnaire &&
                                this.props.coreData?.request?.allowExtensionRequest && this.props.selectedInventory?.revisionRequest === null &&
                                this.props.coreData?.request?.surveyType !== SurveyType.RequestMaterialData_AllowSupplierToSpecifyMaterials &&
                                Object.entries(this.props.coreData?.response?.groupedResponseInventories).every(g => g[1].groupInventoryId !== this.props.selectedInventory?.inventoryId) &&
                                    <ExtensionRequest
                                        inventoryId={this.props.selectedInventory?.inventoryId}
                                        extensionRequest={this.props.coreData?.extensionRequest}
                                        localizer={this.props.appServices}
                                        responseId={this.props.coreData?.response.responseId}
                                        responseOwnerId={this.props.coreData?.response.ownerId}
                                        updateExtensionRequest={this.props.setExtensionRequestAndValidateProduct}
                                        setSupplierActionUnavailable={this.props.setSupplierActionUnavailable}
                                        validationType={this.props.selectedInventory?.validationType}
                                        showButton={this.props.navContext.mainNavIndex !== SurveyPageType.SupplierQuestionnaire}
                                    />
                            }
                    </div>
                    </div>
                </div>
                {info == null && <RevisionRequest revisionRequestInstructions={this.props.pageRevisionInstructions} revisionRequestTitle={this.localizeString("Revision Requests:")} localizer={this.props.appServices} extensibleColumnNames={(this.props.selectedInventory?.extensibleColumnNames ?? [])}></RevisionRequest>}
                <div className="questionnaire-body">
                    {info !== null && <div className="alert alert-warning">{this.localizeString(info)}</div>}
                    <div className="row">
                        <div className={`questions-wrapper col-xs-12 ${shouldMaterialSummaryAppear ? "col-md-6" : ""}`}>
                            {this.isScipPage()
                                ? info == null && <ScipSurveyPage {...this.props} />
                                : this.renderQuestions()}
                        </div>
                        {shouldMaterialSummaryAppear && info === null &&
                            <MaterialSummary complianceScoresResult={this.state.regulationScoresTable}/>
                        }
                    </div>
                </div>
            </>
        )
    }
}

export default connect(
    (state: ApplicationState) => ({ ...state.surveyResponseState, aiSurveyAgentState: state.aiSurveyAgentState, appServices: state.appServices }),
    SurveyResponseController.actionCreators
)(QuestionnairePage as any);
